import type { Block, SpaceEntry } from './types.js'
import { LILODATA_PREFIX } from './constants.js'
import { generateId } from './id.js'

type LilodataFields = {
  paperLabel?: string
  slug?: string
}

export function isLilodataText(text: string): boolean {
  return text.trim().startsWith(LILODATA_PREFIX)
}

export function isLilodataBlock(block: SpaceEntry): block is Block {
  return 'xyzw' in block && isLilodataText(block.text)
}

export function parseLilodataText(text: string): LilodataFields {
  if (!isLilodataText(text)) return {}
  const raw = text.trim().slice(LILODATA_PREFIX.length).trim()
  if (!raw) return {}
  const fields: LilodataFields = {}
  raw.split(/\s+/).forEach(token => {
    const [key, value] = token.split('=')
    if (!key || value === undefined) return
    if (key === 'paper') fields.paperLabel = value
    if (key === 'slug') fields.slug = value
  })
  return fields
}

export function buildLilodataText(paperLabel: string, slug?: string): string {
  const tokens: string[] = []
  if (paperLabel) tokens.push(`paper=${paperLabel}`)
  if (slug) tokens.push(`slug=${slug}`)
  return tokens.length ? `${LILODATA_PREFIX} ${tokens.join(' ')}` : LILODATA_PREFIX
}

export function updateLilodataText(text: string, updates: LilodataFields): string {
  if (!isLilodataText(text)) {
    const label = updates.paperLabel ?? ''
    const slug = updates.slug
    return (label || slug) ? buildLilodataText(label || '', slug) : LILODATA_PREFIX
  }
  const raw = text.trim().slice(LILODATA_PREFIX.length).trim()
  const tokens = raw ? raw.split(/\s+/) : []
  const rawTokens: string[] = []
  const map = new Map<string, string>()
  tokens.forEach(token => {
    const [key, value] = token.split('=')
    if (!key || value === undefined) {
      rawTokens.push(token)
      return
    }
    map.set(key, value)
  })
  if (updates.paperLabel) {
    map.set('paper', updates.paperLabel)
  }
  if (updates.slug) {
    map.set('slug', updates.slug)
  }
  const entries = [...map.entries()].map(([key, value]) => `${key}=${value}`)
  const nextTokens = [...entries, ...rawTokens]
  return nextTokens.length ? `${LILODATA_PREFIX} ${nextTokens.join(' ')}` : LILODATA_PREFIX
}

export function createLilodataBlock(paperLabel: string, opts?: { createdId?: string, editedId?: string }): Block {
  const createdId = opts?.createdId ?? generateId()
  const editedId = opts?.editedId ?? createdId
  return {
    text: buildLilodataText(paperLabel),
    xyzw: [0, 0, 0, 1],
    ided: [createdId, editedId],
    link: []
  }
}

export function extractLilodataBlock<T extends SpaceEntry>(blocks: T[]): { blocks: T[], metaBlock?: Block } {
  let metaBlock: Block | undefined
  const filtered = blocks.filter(block => {
    if (isLilodataBlock(block)) {
      metaBlock = block
      return false
    }
    return true
  })
  return { blocks: filtered, metaBlock }
}
